/**
 * BulkCertificateGenerator Component
 * ===================================
 * A modal component for generating multiple certificates at once.
 * Uses the CompletionCertificate component to generate PDFs.
 * 
 * ⭐ UPDATED: Now downloads all PDFs as a ZIP file with month-based naming
 * 
 * Usage:
 * <BulkCertificateGenerator
 *   visible={modalVisible}
 *   onClose={() => setModalVisible(false)}
 *   agreements={selectedAgreements}
 *   month="2025-12"
 *   onComplete={(results) => console.log(results)}
 * />
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Progress,
  Steps,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Result,
  List,
  message
} from 'antd';
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import CompletionCertificate from './CompletionCertificate';

const { Text } = Typography;
const { Step } = Steps;

// Color constant
const PHOENICIAN_GOLD = '#A2732E';

// Timeouts
const GENERATION_TIMEOUT_MS = 30000; // 30 seconds per certificate
const DELAY_BETWEEN_CERTS_MS = 2000; // 2 seconds between certificates

// Service Type Badge component
const ServiceTypeBadge = ({ serviceType }) => {
  if (!serviceType) return null;
  
  const type = serviceType.toUpperCase();
  if (type.includes('SWIMMING') || type.includes('POOL')) {
    return <Tag color="blue" icon={<SafetyCertificateOutlined />}>Swimming Pool</Tag>;
  }
  if (type.includes('LANDSCAPE') || type.includes('IRRIGATION')) {
    return <Tag color="green" icon={<SafetyCertificateOutlined />}>Landscaping</Tag>;
  }
  return <Tag color="default">{serviceType}</Tag>;
};

const BulkCertificateGenerator = ({
  visible,
  onClose,
  agreements = [],
  month,
  onComplete
}) => {
  // State
  const [step, setStep] = useState(0); // 0: preview, 1: generating, 2: complete
  const [currentIndex, setCurrentIndex] = useState(0);
  const [generatedCerts, setGeneratedCerts] = useState([]);
  const [failedCerts, setFailedCerts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentAgreement, setCurrentAgreement] = useState(null);
  const [generationKey, setGenerationKey] = useState(0);
  
  // Ref for storing generated PDF data
  const generatedPDFs = useRef([]);
  const generateResolveRef = useRef(null);
  const timeoutRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setStep(0);
      setCurrentIndex(0);
      setGeneratedCerts([]);
      setFailedCerts([]);
      setIsGenerating(false);
      setCurrentAgreement(null);
      setGenerationKey(0);
      generatedPDFs.current = [];
      generateResolveRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [visible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle certificate generation complete callback
  const handleCertificateGenerated = useCallback((result) => {
    console.log('[BULK_GEN] 📥 Certificate generated callback received:', result?.success ? 'SUCCESS' : 'FAILED');
    
    // Clear the timeout since we got a response
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (result && result.success) {
      // Store the generated PDF info
      generatedPDFs.current.push({
        agreement: currentAgreement,
        url: result.url,
        filename: result.filename,
        blob: result.blob
      });
      console.log('[BULK_GEN] ✅ PDF stored successfully');
    }
    
    // Resolve the promise to continue to next
    if (generateResolveRef.current) {
      const resolve = generateResolveRef.current;
      generateResolveRef.current = null;
      resolve(result);
    }
  }, [currentAgreement]);

  // Start the generation process
  const startGeneration = async () => {
    if (agreements.length === 0) {
      message.error('No agreements to generate');
      return;
    }

    setStep(1);
    setIsGenerating(true);
    setCurrentIndex(0);
    setGeneratedCerts([]);
    setFailedCerts([]);
    generatedPDFs.current = [];

    console.log(`[BULK_GEN] 🚀 Starting bulk generation for ${agreements.length} certificates`);

    // Process each agreement sequentially
    for (let i = 0; i < agreements.length; i++) {
      const agreement = agreements[i];
      setCurrentIndex(i);
      
      // Force component remount by changing key
      setGenerationKey(prev => prev + 1);
      
      // Small delay to ensure state updates
      await new Promise(r => setTimeout(r, 100));
      
      setCurrentAgreement(agreement);

      console.log(`[BULK_GEN] 📋 Processing ${i + 1}/${agreements.length}: ${agreement.ref_no}`);

      try {
        // Create a promise that will be resolved by handleCertificateGenerated
        const result = await new Promise((resolve) => {
          generateResolveRef.current = resolve;
          
          // Set timeout for generation
          timeoutRef.current = setTimeout(() => {
            console.warn(`[BULK_GEN] ⏰ Generation timeout for: ${agreement.ref_no}`);
            if (generateResolveRef.current) {
              generateResolveRef.current({ success: false, error: 'Generation timeout' });
              generateResolveRef.current = null;
            }
          }, GENERATION_TIMEOUT_MS);
        });

        if (result && result.success) {
          setGeneratedCerts(prev => [...prev, agreement]);
          console.log(`[BULK_GEN] ✅ Success: ${agreement.ref_no}`);
        } else {
          setFailedCerts(prev => [...prev, { ...agreement, error: result?.error || 'Unknown error' }]);
          console.log(`[BULK_GEN] ❌ Failed: ${agreement.ref_no}`);
        }
        
      } catch (error) {
        console.error('[BULK_GEN] ❌ Exception:', agreement.ref_no, error);
        setFailedCerts(prev => [...prev, { ...agreement, error: error.message || 'Unknown error' }]);
      }

      // Clear current agreement
      setCurrentAgreement(null);
      
      // Wait between generations
      console.log(`[BULK_GEN] ⏳ Waiting ${DELAY_BETWEEN_CERTS_MS}ms...`);
      await new Promise(r => setTimeout(r, DELAY_BETWEEN_CERTS_MS));
    }

    // Complete
    setStep(2);
    setIsGenerating(false);
    setCurrentAgreement(null);

    const finalGeneratedCount = generatedPDFs.current.length;
    const finalFailedCount = agreements.length - finalGeneratedCount;

    console.log(`[BULK_GEN] 🏁 Complete: ${finalGeneratedCount} generated, ${finalFailedCount} failed`);

    // Notify parent
    if (onComplete) {
      onComplete({
        generated: finalGeneratedCount,
        failed: finalFailedCount,
        pdfs: generatedPDFs.current
      });
    }

    if (finalGeneratedCount > 0) {
      message.success(`Generated ${finalGeneratedCount} certificates!`);
    }
    
    if (finalFailedCount > 0) {
      message.warning(`${finalFailedCount} certificates failed to generate`);
    }
  };

  // ⭐ DOWNLOAD ALL PDFs AS ZIP
  const downloadAllPDFs = async () => {
    if (generatedPDFs.current.length === 0) {
      message.warning('No PDFs to download');
      return;
    }

    try {
      message.loading('Creating ZIP file...', 0);
      
      // Dynamic import of JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      // Format month for ZIP filename (e.g., "2025-07" -> "July_2025")
      const [year, monthNum] = month.split('-');
      const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      const monthName = monthDate.toLocaleString('default', { month: 'long' });
      const zipFilename = `Certificates_${monthName}_${year}.zip`;
      
      console.log(`[BULK] Creating ZIP: ${zipFilename} with ${generatedPDFs.current.length} files`);
      
      // Add each PDF to the ZIP
      for (const pdf of generatedPDFs.current) {
        if (pdf.blob) {
          zip.file(pdf.filename, pdf.blob);
        }
      }
      
      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Download ZIP
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      message.destroy();
      message.success(`Downloaded ${generatedPDFs.current.length} certificates as ${zipFilename}`);
      
    } catch (error) {
      message.destroy();
      console.error('ZIP creation error:', error);
      message.error('Failed to create ZIP file. JSZip library might not be available.');
    }
  };

  // Download single PDF
  const downloadSinglePDF = (index) => {
    const pdf = generatedPDFs.current[index];
    if (pdf?.url) {
      const link = document.createElement('a');
      link.href = pdf.url;
      link.download = pdf.filename || `certificate_${index + 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Calculate progress
  const progress = agreements.length > 0 
    ? Math.round(((currentIndex + 1) / agreements.length) * 100) 
    : 0;

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined style={{ color: PHOENICIAN_GOLD }} />
          <span>Bulk Certificate Generation</span>
        </Space>
      }
      open={visible}
      onCancel={step === 1 ? null : onClose}
      closable={step !== 1}
      maskClosable={step !== 1}
      width={900}
      footer={null}
      destroyOnClose
    >
      {/* Progress Steps */}
      <Steps current={step} style={{ marginBottom: 24 }}>
        <Step title="Preview" description="Review selection" />
        <Step title="Generating" description="Creating PDFs" />
        <Step title="Complete" description="Download ready" />
      </Steps>

      {/* Step 0: Preview */}
      {step === 0 && (
        <div>
          <Alert
            message={`Ready to generate ${agreements.length} certificates for ${month}`}
            description={
              <div>
                <p>Each certificate will be generated as a PDF using the standard template.</p>
                <p><strong>Estimated time:</strong> ~{Math.ceil(agreements.length * (GENERATION_TIMEOUT_MS/1000 + DELAY_BETWEEN_CERTS_MS/1000) / 60)} minutes</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={agreements}
            columns={[
              { 
                title: '#', 
                key: 'index', 
                width: 50,
                render: (_, __, index) => index + 1 
              },
              { 
                title: 'Cluster', 
                dataIndex: 'cluster', 
                key: 'cluster', 
                ellipsis: true,
                width: 200
              },
              { 
                title: 'PO Number', 
                dataIndex: 'ref_no', 
                key: 'ref_no',
                width: 150
              },
              { 
                title: 'Service Type', 
                key: 'service_type', 
                width: 150,
                render: (_, record) => <ServiceTypeBadge serviceType={record.service_type} /> 
              },
            ]}
            rowKey={(r) => `${r.cluster}-${r.ref_no}`}
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
          />

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={startGeneration}
                style={{ backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }}
              >
                Start Generation ({agreements.length})
              </Button>
            </Space>
          </div>
        </div>
      )}

      {/* Step 1: Generating */}
      {step === 1 && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Progress
              type="circle"
              percent={progress}
              format={() => `${currentIndex + 1}/${agreements.length}`}
              strokeColor={PHOENICIAN_GOLD}
            />
            <div style={{ marginTop: 16 }}>
              <Text strong>
                Generating certificate {currentIndex + 1} of {agreements.length}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Timeout: {GENERATION_TIMEOUT_MS/1000}s per certificate
              </Text>
            </div>
          </div>

          {currentAgreement && (
            <Card size="small" style={{ marginBottom: 16, borderColor: PHOENICIAN_GOLD }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary">Cluster:</Text>
                  <br />
                  <Text strong>{currentAgreement.cluster}</Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">PO Number:</Text>
                  <br />
                  <Text strong>{currentAgreement.ref_no}</Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Service Type:</Text>
                  <br />
                  <ServiceTypeBadge serviceType={currentAgreement.service_type} />
                </Col>
              </Row>
            </Card>
          )}

          {/* Progress list */}
          <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 16 }}>
            <List
              size="small"
              dataSource={[...generatedCerts, ...failedCerts]}
              renderItem={(item) => {
                const isFailed = failedCerts.some(f => f.ref_no === item.ref_no);
                return (
                  <List.Item>
                    <Space>
                      {isFailed ? (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      ) : (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      )}
                      <Text>{item.cluster} - {item.ref_no}</Text>
                      {isFailed && <Tag color="error">{item.error}</Tag>}
                    </Space>
                  </List.Item>
                );
              }}
            />
          </div>

          <Alert
            message="Please wait..."
            description="Certificates are being generated. Do not close this window."
            type="warning"
            showIcon
            icon={<LoadingOutlined />}
          />

          {/* Hidden certificate generator */}
          {currentAgreement && (
            <div style={{ 
              position: 'fixed', 
              left: '-9999px', 
              top: '0px',
              width: '210mm',
              minHeight: '297mm',
              background: 'white',
              zIndex: -1
            }}>
              <CompletionCertificate
                key={`bulk-${generationKey}-${currentAgreement.cluster}-${currentAgreement.ref_no}-${month}`}
                cluster={currentAgreement.cluster}
                agreementRef={currentAgreement.ref_no}
                month={month}
                serviceType={currentAgreement.service_type}
                payee={currentAgreement.payee}
                previewOnly={false}
                autoGenerate={true}
                onGenerated={handleCertificateGenerated}
              />
            </div>
          )}
        </div>
      )}

      {/* Step 2: Complete */}
      {step === 2 && (
        <div>
          <Result
            status={generatedPDFs.current.length > 0 ? "success" : "warning"}
            title="Certificate Generation Complete!"
            subTitle={
              <span>
                Successfully generated <strong>{generatedPDFs.current.length}</strong> certificates.
                {failedCerts.length > 0 && (
                  <span style={{ color: '#ff4d4f' }}>
                    {' '}{failedCerts.length} failed.
                  </span>
                )}
              </span>
            }
            extra={[
              <Button
                key="download"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadAllPDFs}
                disabled={generatedPDFs.current.length === 0}
                style={{ backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }}
                size="large"
              >
                Download All as ZIP ({generatedPDFs.current.length})
              </Button>,
              <Button key="close" onClick={onClose} size="large">
                Close
              </Button>
            ]}
          />

          {/* Success list */}
          {generatedCerts.length > 0 && (
            <Card size="small" title="Generated Certificates" style={{ marginTop: 16 }}>
              <List
                size="small"
                dataSource={generatedCerts}
                renderItem={(item, index) => (
                  <List.Item
                    actions={[
                      <Button
                        key="download"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadSinglePDF(index)}
                      >
                        Download
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FilePdfOutlined style={{ fontSize: 24, color: PHOENICIAN_GOLD }} />}
                      title={item.ref_no}
                      description={item.cluster}
                    />
                  </List.Item>
                )}
                pagination={generatedCerts.length > 5 ? { pageSize: 5 } : false}
              />
            </Card>
          )}

          {/* Failed list */}
          {failedCerts.length > 0 && (
            <Alert
              message="Some certificates failed to generate"
              description={
                <List
                  size="small"
                  dataSource={failedCerts}
                  renderItem={(item) => (
                    <List.Item>
                      <Text type="danger">
                        {item.cluster} - {item.ref_no}: {item.error}
                      </Text>
                    </List.Item>
                  )}
                />
              }
              type="error"
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default BulkCertificateGenerator;